import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { VideoPhone } from "@/components/public/video-phone"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import { RelatedPages } from "@/components/public/related-pages"
import { relatedFor } from "@/data/related-pages"
import type { SeoMeta } from "@/types"

export type VersusComparisonRow = {
  label: string
  competitor: string
  quiteright: string
  highlight?: boolean
}

export type VersusStat = {
  value: string
  label: string
  detail: string
}

export type VersusFaq = { q: string; a: string }

export type VersusWorkflow = {
  competitorSteps: string[]
  competitorTotal: string
  quiterightSteps: string[]
  quiterightTotal: string
}

export type VersusPageProps = {
  meta: SeoMeta
  competitorName: string
  competitorShortName?: string
  breadcrumbName: string
  heroEyebrow?: string
  heroHeadline: string
  heroAccent?: string
  heroSubhead: string
  statsEyebrow?: string
  statsHeadline: string
  statsSubhead: string
  stats: VersusStat[]
  workflowHeadline: string
  workflow: VersusWorkflow
  comparison: VersusComparisonRow[]
  faq: VersusFaq[]
  ctaHeadline: string
  ctaBody: string
  datePublished?: string
  dateModified?: string
  relatedKey?: string
}

export default function VersusPage({
  meta,
  competitorName,
  competitorShortName,
  breadcrumbName,
  heroEyebrow = "Compare",
  heroHeadline,
  heroAccent = ".",
  heroSubhead,
  statsEyebrow = "Purpose-built",
  statsHeadline,
  statsSubhead,
  stats,
  workflowHeadline,
  workflow,
  comparison,
  faq,
  ctaHeadline,
  ctaBody,
  datePublished = "2026-04-18",
  dateModified = "2026-04-18",
  relatedKey,
}: VersusPageProps) {
  const shortName = competitorShortName ?? competitorName
  const related = relatedKey ? relatedFor(relatedKey) : []

  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />

        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={meta.image} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.image} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: `QuiteRight vs ${competitorName} for Property Videos`,
            description: meta.description,
            publisher: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://www.quiteright.co.uk",
              logo: {
                "@type": "ImageObject",
                url: "https://www.quiteright.co.uk/qr-logo-wordmark-red.png",
              },
            },
            datePublished,
            dateModified,
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          })}
        </script>

        <BreadcrumbSchema canonical={meta.canonical} items={[{ name: breadcrumbName }]} />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-6xl">
          <div className="grid items-center gap-12 md:grid-cols-2">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
                {heroEyebrow}
              </p>
              <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl">
                {heroHeadline}
                <span className="text-red">{heroAccent}</span>
              </h1>
              <p className="mt-6 max-w-lg text-lg text-slate">{heroSubhead}</p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <Link
                  href="/sign-up"
                  className="inline-flex items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-red/90"
                >
                  Start your free trial
                </Link>
                <Link
                  href="/demo"
                  className="inline-flex items-center justify-center rounded-md border border-stone bg-white px-6 py-3 text-base font-medium text-charcoal transition-colors hover:bg-stone"
                >
                  Book a demo
                </Link>
              </div>
            </div>
            <div className="relative flex justify-center">
              <VideoPhone />
              <div className="absolute -bottom-4 -left-4 rounded-lg bg-charcoal px-4 py-2.5 shadow-md md:-left-2">
                <p className="text-xs font-medium text-warm-grey">Created in</p>
                <p className="font-display text-xl font-bold text-white">60 seconds</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              {statsEyebrow}
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              {statsHeadline}
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-center text-base text-slate">
              {statsSubhead}
            </p>
          </ScrollReveal>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {stats.map((stat, i) => (
              <ScrollReveal key={i}>
                <div className="rounded-xl border border-stone bg-white p-6 text-center">
                  <p className="font-display text-4xl font-bold text-charcoal">
                    {stat.value}
                  </p>
                  <p className="mt-1 text-sm font-semibold text-red">
                    {stat.label}
                  </p>
                  <p className="mt-3 text-xs leading-relaxed text-slate">
                    {stat.detail}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow comparison */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              The workflow
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              {workflowHeadline}
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 grid gap-6 md:grid-cols-2">
            <ScrollReveal>
              <div className="flex h-full flex-col rounded-xl border border-stone bg-white p-6 md:p-8">
                <h3 className="font-display text-lg font-bold text-charcoal">
                  With {shortName}
                  <span className="text-red">:</span>
                </h3>
                <ol className="mt-6 flex-1 space-y-4 text-sm leading-relaxed text-slate">
                  {workflow.competitorSteps.map((step, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-stone text-xs font-semibold text-slate">
                        {i + 1}
                      </span>
                      <span className="pt-0.5">{step}</span>
                    </li>
                  ))}
                </ol>
                <div className="mt-6 rounded-lg bg-stone/50 px-4 py-3 text-center">
                  <p className="text-xs font-medium uppercase tracking-[0.15em] text-slate">
                    Total time
                  </p>
                  <p className="mt-1 font-display text-xl font-bold text-charcoal">
                    {workflow.competitorTotal}
                  </p>
                </div>
              </div>
            </ScrollReveal>
            <ScrollReveal>
              <div className="flex h-full flex-col rounded-xl border-2 border-red/20 bg-red/5 p-6 md:p-8">
                <h3 className="font-display text-lg font-bold text-charcoal">
                  With QuiteRight
                  <span className="text-red">:</span>
                </h3>
                <ol className="mt-6 flex-1 space-y-4 text-sm leading-relaxed text-slate">
                  {workflow.quiterightSteps.map((step, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-red text-xs font-semibold text-white">
                        {i + 1}
                      </span>
                      <span className="pt-0.5">{step}</span>
                    </li>
                  ))}
                </ol>
                <div className="mt-6 rounded-lg bg-red/10 px-4 py-3 text-center">
                  <p className="text-xs font-medium uppercase tracking-[0.15em] text-red">
                    Total time
                  </p>
                  <p className="mt-1 font-display text-xl font-bold text-charcoal">
                    {workflow.quiterightTotal}
                  </p>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Side by side
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              The full comparison
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12">
            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[540px] text-left">
                    <thead>
                      <tr className="border-b-2 border-stone">
                        <th className="pb-3 pr-4 text-xs font-medium uppercase tracking-[0.15em] text-slate">
                          Feature
                        </th>
                        <th className="pb-3 px-4 text-center text-xs font-medium uppercase tracking-[0.15em] text-slate">
                          {shortName}
                        </th>
                        <th className="pb-3 pl-4 text-center text-xs font-medium uppercase tracking-[0.15em] text-red">
                          QuiteRight
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparison.map((row, i) => (
                        <tr
                          key={i}
                          className={`border-b border-stone/50 ${row.highlight ? "bg-cream/50" : ""}`}
                        >
                          <td className="py-3.5 pr-4 text-sm font-medium text-charcoal">
                            {row.label}
                          </td>
                          <td className="py-3.5 px-4 text-center text-sm text-slate">
                            {row.competitor}
                          </td>
                          <td className="py-3.5 pl-4 text-center text-sm font-semibold text-charcoal">
                            {row.quiteright}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              FAQ
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common questions
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12">
            <ScrollReveal>
              <dl className="divide-y divide-stone">
                {faq.map((item, i) => (
                  <details key={i} className="group">
                    <summary className="flex cursor-pointer items-start justify-between gap-4 py-5 text-left [&::-webkit-details-marker]:hidden">
                      <dt className="text-sm font-semibold leading-relaxed text-charcoal">
                        {item.q}
                      </dt>
                      <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-stone text-slate transition-transform duration-200 group-open:rotate-45">
                        <svg
                          viewBox="0 0 16 16"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                          className="size-3"
                        >
                          <path d="M8 3v10M3 8h10" />
                        </svg>
                      </span>
                    </summary>
                    <dd className="pb-5 text-sm leading-relaxed text-slate">
                      {item.a}
                    </dd>
                  </details>
                ))}
              </dl>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {related.length > 0 && <RelatedPages links={related} tone="white" />}

      {/* CTA */}
      <section className="border-t border-stone bg-cream px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <ScrollReveal>
            <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
              {ctaHeadline}
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-lg text-slate">{ctaBody}</p>
            <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                href="/sign-up"
                className="inline-flex w-full items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-red/90 sm:w-auto"
              >
                Start your free trial
              </Link>
              <Link
                href="/demo"
                className="inline-flex w-full items-center justify-center rounded-md border border-stone bg-white px-6 py-3 text-base font-medium text-charcoal transition-colors hover:bg-stone sm:w-auto"
              >
                Book a demo
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </PublicLayout>
  )
}
